import {
    Button,
    ButtonGroup,
    Col,
    Form,
    InputGroup,
    ListGroup,
    OverlayTrigger,
    Row,
    Tooltip,
} from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import React, { Fragment, useState } from 'react'
import OutputButton from './actions/OutputButton'
import { Sidebar } from 'primereact/sidebar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSearch,
    faFilterCircleXmark,
    faTableColumns,
} from '@fortawesome/free-solid-svg-icons'
import type { ToolbarConfigButton } from '../data-table/DataTables.d'

/** Minimal column shape for toggle-columns options */
type ColumnOption = {
    field: string
    header?: string
    toggleVisibility?: boolean
}

/** Internal shape for toggle-columns (ToolbarConfigButton with required columnsOptions) */
type ToolbarButtonToggleColumns = ToolbarConfigButton & {
    type: 'toggle-columns'
    columnsOptions: {
        columns: ColumnOption[]
        visibleColumns: ColumnOption[]
        setVisibleColumns: (cols: ColumnOption[]) => void
    }
}

/** Internal shape for export (matches ToolbarConfigButton) */
type ToolbarButtonExport = ToolbarConfigButton & { type: 'export' }

type ToolbarButton = ToolbarButtonToggleColumns | ToolbarButtonExport

type ToolbarProps = {
    globalFilterValue: string
    onGlobalFilterChange: (value: string) => void
    clearFilters: () => void
    config: { includeSearchBox?: boolean; buttons?: ToolbarConfigButton[] }
    leftContent?: React.ReactNode
    hasActiveFilters?: boolean
}

const Toolbar = ({
    globalFilterValue,
    onGlobalFilterChange,
    clearFilters,
    config,
    leftContent = null,
    hasActiveFilters = false,
}: ToolbarProps) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const { includeSearchBox, buttons = [] } = config

    const [searchTerm, setSearchTerm] = useState(globalFilterValue)
    const [visibleRight, setVisibleRight] = useState(false)

    const hasSearchText = searchTerm != null && String(searchTerm).trim() !== ''
    const canSearch = hasSearchText
    const canClear = hasActiveFilters || hasSearchText

    const btnVariant = theme === 'dark' ? 'dark' : 'light'

    const searchInputGroup = includeSearchBox && (
        <InputGroup size="sm" className="shadow datatable-filter-input-group">
            <Form.Control
                type="text"
                placeholder="Keywords.."
                aria-label="Keywords.."
                aria-describedby="SearchTableBtn"
                className="border-0"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
                variant={btnVariant}
                id="SearchTableBtn"
                className="border-0"
                disabled={!canSearch}
                onClick={() => onGlobalFilterChange(searchTerm)}
            >
                <FontAwesomeIcon icon={faSearch} className="me-2" />
                Search
            </Button>
        </InputGroup>
    )

    const clearFiltersButton = includeSearchBox && (
        <OverlayTrigger
            placement="bottom"
            overlay={
                <Tooltip id="tooltip-clear-filters">Clear Filters</Tooltip>
            }
        >
            <span className="d-inline-block">
                <Button
                    variant={btnVariant}
                    size="sm"
                    className="shadow"
                    disabled={!canClear}
                    onClick={() => {
                        clearFilters()
                        setSearchTerm('')
                        onGlobalFilterChange('')
                    }}
                    aria-label="Clear Filters"
                >
                    <FontAwesomeIcon icon={faFilterCircleXmark} />
                </Button>
            </span>
        </OverlayTrigger>
    )

    const buttonsContent = (
        <ButtonGroup size="sm" className="shadow">
            {(buttons as ToolbarButton[]).map((button, index) => (
                <Fragment key={index}>
                    {(() => {
                        switch (button.type) {
                            case 'toggle-columns': {
                                const opts = button.columnsOptions
                                return (
                                    <>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip id={`tooltip-toggle-columns-${index}`}>
                                                    {button.label}
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant={btnVariant}
                                                size="sm"
                                                onClick={() =>
                                                    setVisibleRight(true)
                                                }
                                                aria-label={button.label}
                                            >
                                                <FontAwesomeIcon icon={faTableColumns} />
                                            </Button>
                                        </OverlayTrigger>
                                        <Sidebar
                                            visible={visibleRight}
                                            position="right"
                                            header="Show/Hide columns"
                                            onHide={() =>
                                                setVisibleRight(false)
                                            }
                                        >
                                            <ListGroup>
                                                {opts.columns.map(
                                                    (col: ColumnOption, colIndex: number) => {
                                                        const thisField =
                                                            opts.visibleColumns.find(
                                                                (obj: ColumnOption) =>
                                                                    obj.field === col.field
                                                            )
                                                        const toggleVisibility =
                                                            typeof col.toggleVisibility ===
                                                                'undefined' ||
                                                            col.toggleVisibility
                                                        return (
                                                            <ListGroup.Item
                                                                key={colIndex}
                                                                className={
                                                                    toggleVisibility
                                                                        ? 'd-block'
                                                                        : 'd-none'
                                                                }
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    value={col.field}
                                                                    disabled={
                                                                        !toggleVisibility
                                                                    }
                                                                    onChange={(
                                                                        e: React.ChangeEvent<HTMLInputElement>
                                                                    ) => {
                                                                        let selectedColumns: ColumnOption[] =
                                                                            [...opts.visibleColumns]

                                                                        if (
                                                                            !e.target.checked
                                                                        ) {
                                                                            selectedColumns =
                                                                                opts.visibleColumns.filter(
                                                                                    (_col: ColumnOption) =>
                                                                                        _col.field !== col.field
                                                                                )
                                                                        } else {
                                                                            selectedColumns.push(col)
                                                                        }

                                                                        const orderedSelectedColumns =
                                                                            opts.columns.filter(
                                                                                (_col: ColumnOption) =>
                                                                                    selectedColumns.some(
                                                                                        (sCol: ColumnOption) =>
                                                                                            sCol.field === _col.field
                                                                                    )
                                                                            )
                                                                        opts.setVisibleColumns(
                                                                            orderedSelectedColumns
                                                                        )
                                                                    }}
                                                                    defaultChecked={
                                                                        thisField != null &&
                                                                        thisField.field === col.field
                                                                    }
                                                                    id={col.field}
                                                                    label={col.header}
                                                                    role="button"
                                                                />
                                                            </ListGroup.Item>
                                                        )
                                                    }
                                                )}
                                            </ListGroup>
                                        </Sidebar>
                                    </>
                                )
                            }
                            case 'export':
                                return (
                                    <OutputButton
                                        label={button.label}
                                        exportTypes={button.exportTypes ?? []}
                                    />
                                )
                            default: {
                                const _exhaustive: never = button
                                throw new Error('Invalid button type!')
                            }
                        }
                    })()}
                </Fragment>
            ))}
        </ButtonGroup>
    )

    if (leftContent) {
        return (
            <div
                className="datatable-toolbar-bar datatable-results-summary d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-2 gap-md-0 px-3 py-2 rounded"
                data-testid="datatable-results-summary"
            >
                <div className="datatable-toolbar-summary d-flex align-items-center gap-2 flex-shrink-0 order-1 order-md-1">
                    {leftContent}
                </div>
                <div className="datatable-toolbar-actions d-flex flex-wrap align-items-center gap-2 order-2 order-md-2 ms-md-auto flex-shrink-0">
                    <div className="datatable-toolbar-search-wrap flex-grow-1 flex-md-grow-0">
                        {searchInputGroup}
                    </div>
                    <div className="datatable-toolbar-icons d-flex align-items-center gap-1">
                        {clearFiltersButton}
                        {buttonsContent}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Row className="align-items-center flex-nowrap g-2">
            {includeSearchBox && (
                <Col lg={6} className="d-flex align-items-center gap-2">
                    {searchInputGroup}
                    {clearFiltersButton}
                </Col>
            )}
            <Col lg={6}>
                {buttonsContent}
            </Col>
        </Row>
    )
}

export default Toolbar
